import { useState, useRef, useEffect, useLayoutEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Sheet, SheetContent, SheetTitle, SheetDescription, SheetClose } from "@/components/ui/sheet";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SignedImg } from "@/components/shared/SignedImg";
import { CloseButton, useIsMobile } from "@/components/shared/PagePrimitives";
import { api, formatApiError } from "@/lib/api";
import { todayISO } from "@/lib/days";
import { formatMoneyExact as formatCZK, formatMoney, formatDate, plur } from "@/lib/format";
import { Briefcase, CheckCircle2, ChevronLeft, FileText, Layers, Minus, Plus, Shapes, Trash2, Undo2 } from "lucide-react";
import { OffsetDialog } from "@/pages/EconomicsPayments";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { NotInvoiceHint } from "@/components/shared/NotInvoiceHint";
import { kindBlocked, pendingCurrency, TAX_DOC, applyDocType, payableTotal, lineWithAmount, lineUnitPrice, linesTotals, docWithLines, headerFollowsLines, realLines, VAT_RATES } from "@/lib/documents";
import { AdvancesSection, TaxDocPicker, TaxDocNote } from "@/components/shared/AdvancesSection";
import { DirectionToggle, DocKindRow } from "@/components/shared/DocKind";
import { NumInput } from "@/components/shared/NumInput";
import { DocPartyCards } from "@/components/shared/DocPartyCards";
import { ask } from "@/lib/confirm";
import { OrderLinkCard } from "@/components/orders/OrderLinkCard";
import { useDetailNav, DetailNav } from "@/components/shared/ListNav";
import { PropRow, PropSelect, propHotkey } from "@/components/shared/PropRow";
import { useAuth } from "@/context/AuthContext";
import { DocTagEyebrow, TagsManagerDialog } from "@/components/economics/DocTags";
import { useDockEscape } from "@/components/shared/DetailDock";
import { AmountTiles, AmountTile } from "@/components/shared/AmountTiles";
import { autoPill } from "@/components/shared/Pill";

const moneyFor = (doc) => (v) => formatCZK(v, pendingCurrency(doc));
// 375: u faktury částky v celých korunách (haléře jen u jednotkové ceny, rozdílu a v polích; účtenka je má dál)
const wholeFor = (doc) => (v) => formatMoney(v, pendingCurrency(doc));
const whole = (v) => formatMoney(v);

// 253: pozdní faktura jde i na uzavřenou zakázku — ve výběru je až za rozpracovanými a se štítkem
const CLOSED = ["Dokončeno", "Fakturováno", "Záruka"];
const projLabel = (p) => (CLOSED.includes(p.status) ? `${p.name} · uzavřená` : p.name);
const openFirst = (list) => [...(list || []).filter((p) => !CLOSED.includes(p.status)), ...(list || []).filter((p) => CLOSED.includes(p.status))];
// 376: zakázky do výběru s hledáním — hledá se i podle čísla a adresy (pod názvem), uzavřené na konci
export const projectOptions = (projects, current, noneLabel) => [
  { value: null, label: noneLabel, on: !current },
  ...openFirst(projects).map((p) => ({ value: p.id, label: p.name, on: current === p.id,
    sub: [p.code, p.address, CLOSED.includes(p.status) && "uzavřená"].filter(Boolean).join(" · ") || undefined })),
];
export const catOptions = (cats, current) => cats.map((c, i) => ({ value: c, label: c, on: c === current, key: i < 9 ? String(i + 1) : undefined }));

const METHODS = ["Převodem", "Hotově", "Kartou"];
const today = todayISO;   // 401: místní den — toISOString je UTC, platba po půlnoci spadla do předchozího dne
const REASON_WORD = { ico: "IČO", dic: "DIČ", name: "název" };

// Řádek pod „Typ": proč systém předvyplnil vydanou/přijatou (zadání 148).
// Bez IČO firmy nemá podle čeho poznat, že dodavatel na dokladu je uživatel sám.
export function DirectionHint({ doc }) {
  if (!doc || doc.id) return null;
  const why = REASON_WORD[doc.direction_reason] || "IČO";
  if (doc.direction === "vydana") {
    return <p className="text-xs text-muted-foreground mt-1" data-testid="direction-hint">Rozpoznáno jako vydaná faktura — dodavatel na dokladu je vaše firma ({why}).</p>;
  }
  if (doc.direction === "prijata") {
    return <p className="text-xs text-muted-foreground mt-1" data-testid="direction-hint">Rozpoznáno jako přijatá faktura — odběratel je vaše firma ({why}).</p>;
  }
  if (doc.direction == null && doc.company_has_ico === false) {
    return (
      <p className="text-xs text-muted-foreground mt-1" data-testid="direction-hint">
        Doplňte IČO firmy v <Link to="/nastaveni" className="text-brand hover:underline font-semibold" data-testid="direction-hint-settings">Nastavení</Link>, systém pak sám pozná vydané faktury.
      </p>
    );
  }
  return null;
}

// Řádek pod štítkem účtenky: OCR přes „Nahrát fakturu" poznalo účtenku (150).
export function KindSwitchedHint({ rec, onOpenAsInvoice }) {
  if (!rec?.ocr_kind_switched || rec.id) return null;
  return (
    <p className="text-xs text-muted-foreground" data-testid="kind-switched-hint">
      Rozpoznáno jako účtenka — bylo nahráno přes Nahrát fakturu. Je to faktura?{" "}
      <button type="button" onClick={onOpenAsInvoice} className="text-brand hover:underline font-semibold" data-testid="open-as-invoice">→ Otevřít jako fakturu</button>
    </p>
  );
}

// Detail dokladu (229) — „Kontrola dokladu" podle varianty A: na PC vyjede zprava široký
// panel (vlevo náhled faktury, vpravo údaje), na telefonu zespodu s přepínačem Údaje /
// Faktura. Řízené rodičem: rodič drží `doc`, uložení i zahození. Používají ho
// Ekonomika → Doklady i záložka Ekonomika v detailu zakázky.
// 371: pole v řádku položky bez rámečku — rámeček až po najetí myší / v poli
const GHOST = "border-transparent bg-transparent shadow-none hover:border-border focus:border-border";
const PROJECT_COLORS = ["#e0703f", "#5b8def", "#1bae8a", "#d49a12", "#9b6be8", "#e5484d", "#2bb5c8"];
const REZIE_COLOR = "#9aa1ad";
const STATUS_PILL = {
  "Zaplaceno": "bg-success/15 text-success", "Vyrovnáno": "bg-success/15 text-success",
  "Částečně vyrovnáno": "bg-sky-500/15 text-sky-700 dark:text-sky-300", "Čeká na vratku": "bg-sky-500/15 text-sky-700 dark:text-sky-300",
  "Vrátit klientovi": "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  "Částečně zaplaceno": "bg-sky-500/15 text-sky-700 dark:text-sky-300",
  "Nezaplaceno": "bg-secondary text-muted-foreground",
};
const nf = (v) => Number(String(v ?? "").replace(",", ".")) || 0;
const daysTo = (iso) => {
  if (!iso) return null;
  const a = new Date(`${iso.slice(0, 10)}T12:00:00`), b = new Date(`${today()}T12:00:00`);
  return Math.round((a - b) / 86400000);
};
const shortDate = (iso) => (iso ? `${Number(iso.slice(8, 10))}. ${Number(iso.slice(5, 7))}.` : "");

function Field({ label, children, className = "" }) {
  return <div className={`min-w-0 ${className}`}><Label className="text-[13px] text-muted-foreground font-medium">{label}</Label><div className="mt-1">{children}</div></div>;
}

// Náhled souboru: přiblížit / oddálit / stáhnout. PDF má vlastní prohlížeč v SignedImg. 372: i fotka účtenky.
export function Preview({ doc, onHide }) {
  const [zoom, setZoom] = useState(1);
  const isPdf = (doc.file_url || "").toLowerCase().includes(".pdf");
  const name = (doc.file_url || "").split("/").pop()?.split("?")[0] || "doklad";
  const btn = "w-10 h-10 rounded-full bg-card border border-border grid place-items-center hover:bg-secondary disabled:opacity-40";
  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="flex items-center gap-2 px-4 py-3 shrink-0">
        <span className="text-sm font-semibold truncate flex-1 min-w-0" title={name}>{doc.file_url ? name : "Bez přílohy"}</span>
        {doc.file_url && !isPdf && (
          <>
            <button type="button" className={btn} onClick={() => setZoom((z) => Math.max(1, z - 0.25))} disabled={zoom <= 1} aria-label="Oddálit"><Minus size={17} /></button>
            <button type="button" className={btn} onClick={() => setZoom((z) => Math.min(3, z + 0.25))} aria-label="Přiblížit"><Plus size={17} /></button>
          </>
        )}
        {onHide && <button type="button" className={`${btn} hidden md:grid`} onClick={onHide} aria-label="Skrýt náhled" title="Skrýt náhled"><ChevronLeft size={18} /></button>}
      </div>
      <div className="flex-1 min-h-0 overflow-auto px-4 pb-4">
        {doc.file_url ? (
          <div style={{ width: `${zoom * 100}%` }} className="transition-[width] motion-reduce:transition-none">
            <SignedImg url={doc.file_url} alt="doklad" className="w-full rounded-[12px] overflow-hidden bg-white shadow-sm" testid="doc-image" />
          </div>
        ) : (
          <div className="w-full min-h-[260px] h-full rounded-[14px] bg-card border border-border grid place-items-center" data-testid="doc-no-file">
            <div className="flex flex-col items-center gap-2 text-muted-foreground p-6 text-center">
              <FileText size={32} />
              <span className="text-sm font-semibold">Bez přílohy — doklad zadán ručně</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Úhrady přímo v detailu: Zaplatit zbytek jedním klikem (s Vrátit), Přidat úhradu rozbalí
// formulář, seznam úhrad s košem. Endpointy vrací aktualizovaný doklad (157/B).
function PaymentBox({ doc, setDoc, issued, total, paid, onChanged }) {
  const credit = !!doc.is_credit_note;
  const [offset, setOffset] = useState(null);
  const base = issued ? "/issued-invoices" : "/documents";
  const rest = Math.max(total - paid, 0);
  const [form, setForm] = useState(null);
  const [undo, setUndo] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(null); setUndo(null); }, [doc.id]);
  // 412: K úhradě 0 (vyúčtování pokryté zálohou) — faktura je vyrovnaná, platit se nic nemá
  const settled = !credit && total < 0.5 && (Number(doc.total_with_vat) || Number(doc.total_without_vat) || 0) > 0.5;
  const status = settled || (rest < 0.5 && total > 0) ? (credit ? "Vyrovnáno" : "Zaplaceno") : paid > 0 ? (credit ? "Částečně vyrovnáno" : "Částečně zaplaceno") : (credit ? (issued ? "Vrátit klientovi" : "Čeká na vratku") : "Nezaplaceno");
  const dueIn = daysTo(doc.due_date);
  const duePill = rest < 0.5 || credit || dueIn == null ? null
    : dueIn < 0 ? { t: `${-dueIn} ${-dueIn === 1 ? "den" : -dueIn < 5 ? "dny" : "dní"} po splatnosti`, c: "bg-destructive/10 text-destructive" }
      : dueIn === 0 ? { t: `Splatnost dnes ${shortDate(doc.due_date)}`, c: "bg-amber-500/15 text-amber-700 dark:text-amber-300" }
        : { t: `Splatnost ${shortDate(doc.due_date)}`, c: "bg-secondary text-muted-foreground" };
  const post = async (body, msg) => {
    setBusy(true);
    try {
      const { data } = await api.post(`${base}/${doc.id}/payments`, body);
      const added = (data.payments || []).find((p) => !(doc.payments || []).some((q) => q.id === p.id));
      onChanged(data);
      setUndo(added ? { id: added.id, text: msg(data) } : null);
      setForm(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const payRest = () => post(credit
    ? { date: today(), amount: Math.round(rest * 100) / 100, method: "Vratka", note: issued ? "vratka klientovi" : "vratka od dodavatele" }
    : { date: today(), amount: Math.round(rest * 100) / 100, method: "Převodem" },
  () => credit ? `Vratka ${whole(rest)} zapsána · dobropis je vyrovnaný` : `${issued ? "Inkasováno" : "Doplaceno"} ${whole(rest)} · faktura je ${issued ? "inkasovaná" : "zaplacená"}`);
  const openOffset = async () => {
    try {
      const { data } = await api.get(issued ? "/issued-invoices" : "/documents?view=list");
      setOffset({ credit: { ...doc, paid_total: paid, ...(issued ? { client_name: doc.supplier_name, client_ico: doc.supplier_ico } : {}) }, coll: issued ? "issued_invoices" : "documents", list: data });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveForm = () => {
    const amount = nf(form.amount);
    if (!(amount > 0)) return toast.error("Zadejte částku.");
    if (amount > rest + 0.5) return toast.error(`Úhrada přesahuje zbývající částku — zbývá ${whole(rest)}.`);
    post({ date: form.date, amount, method: form.method }, (d) => `Úhrada ${whole(amount)} zapsána · zbývá ${whole(Math.max((d.amount_due ?? total) - (d.paid_total ?? 0), 0))}`);
  };
  const doUndo = async () => {
    try { const { data } = await api.delete(`${base}/${doc.id}/payments/${undo.id}`); onChanged(data); setUndo(null); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const over = form && nf(form.amount) > rest + 0.5;
  return (
    <section className="rounded-[18px] border border-border bg-card" data-testid="doc-payments-section">
      <div className="flex flex-wrap items-center gap-2 p-3 md:p-4">
        <span className={`rounded-full px-3 py-1 text-[13px] font-semibold ${STATUS_PILL[status]}`} data-testid="doc-pay-status">{status}</span>
        {duePill && <span className={`rounded-full px-3 py-1 text-[13px] font-semibold ${duePill.c}`}>{duePill.t}</span>}
        <span className="flex-1" />
        {rest > 0.5 && !form && credit && (
          <div className="grid grid-cols-2 gap-2 w-full md:flex md:w-auto">
            <Button type="button" variant="outline" onClick={openOffset} disabled={busy} className="h-11 md:h-10 rounded-full font-semibold" data-testid="doc-offset">Započíst na fakturu</Button>
            <Button type="button" variant="outline" onClick={() => setForm({ amount: "", date: today(), method: "Vratka" })} disabled={busy} className="h-11 md:h-10 rounded-full font-semibold" data-testid="doc-add-payment"><Plus size={16} /> Přidat vratku</Button>
            <Button type="button" onClick={payRest} disabled={busy} className="h-11 md:h-10 rounded-full font-semibold whitespace-nowrap col-span-2 order-first md:order-none" data-testid="doc-pay-rest">
              {issued ? "Vráceno klientovi" : "Vratka přišla"} {whole(rest)}</Button>
          </div>
        )}
        {rest > 0.5 && !form && !credit && (
          <div className="grid grid-cols-1 gap-2 w-full md:flex md:w-auto">
            <Button type="button" variant="outline" onClick={() => setForm({ amount: "", date: today(), method: "Převodem" })} disabled={busy}
              className="h-11 md:h-10 rounded-full font-semibold" data-testid="doc-add-payment"><Plus size={16} /> Přidat úhradu</Button>
            <Button type="button" onClick={payRest} disabled={busy} className="h-11 md:h-10 rounded-full font-semibold whitespace-nowrap order-first md:order-none" data-testid="doc-pay-rest">
              {issued ? "Inkasovat" : "Zaplatit"} zbytek {whole(rest)}</Button>
          </div>
        )}
      </div>
      {form && (
        <div className="border-t border-border p-3 md:p-4 space-y-3" data-testid="doc-pay-form">
          <div className="grid grid-cols-2 md:grid-cols-[1.2fr_1fr_1fr] gap-2.5">
            <Field label="Částka" className="col-span-2 md:col-span-1">
              <Input inputMode="decimal" autoFocus value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className={`h-11 text-right tabular-nums text-base md:text-sm ${over ? "border-destructive" : ""}`} data-testid="pay-amount" /></Field>
            <Field label={issued ? "Datum příjmu" : "Datum úhrady"}>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-11 w-full min-w-0 text-base md:text-sm" data-testid="pay-date" /></Field>
            <Field label="Způsob">
              <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                <SelectTrigger className="h-11" data-testid="pay-method"><SelectValue /></SelectTrigger>
                <SelectContent>{[...METHODS, "Vratka"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select></Field>
          </div>
          <div className={`text-[13px] ${over ? "text-destructive" : "text-muted-foreground"}`}>
            {over ? `Částka je vyšší, než zbývá (${whole(rest)}).` : <>Zbývá {whole(rest)} · <button type="button" className="text-brand font-semibold hover:underline" onClick={() => setForm({ ...form, amount: String(Math.round(rest * 100) / 100) })} data-testid="pay-fill-rest">vyplnit celý zbytek</button></>}
          </div>
          <div className="flex flex-col-reverse md:flex-row md:justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setForm(null)} className="h-11 w-full md:w-auto">Zrušit</Button>
            <Button type="button" onClick={saveForm} disabled={busy || over} className="h-11 w-full md:w-auto font-bold" data-testid="add-payment-btn">Uložit úhradu</Button>
          </div>
        </div>
      )}
      {!(doc.payments || []).length && !form && (
        <div className="border-t border-border px-3 md:px-4 py-2.5 text-[13px] text-muted-foreground">
          {settled ? "Nic k úhradě — K úhradě 0 Kč, faktura je vyrovnaná zálohou." : credit ? (issued ? "Klientovi zatím nic nevráceno." : "Dodavatel zatím nic nevrátil.") : issued ? "Od odběratele zatím nepřišla žádná platba." : "Zatím žádná úhrada."}</div>
      )}
      {(doc.payments || []).length > 0 && (
        <div className="border-t border-border divide-y divide-border">
          {(doc.payments || []).map((p) => (
            <div key={p.id} className="flex items-center gap-3 px-3 md:px-4 py-2.5 text-sm" data-testid={`payment-${p.id}`}>
              <span className="tabular-nums w-24 shrink-0 text-muted-foreground">{formatDate(p.date)}</span>
              <span className="flex-1 min-w-0 truncate">{p.method || ""}{p.note ? ` · ${p.note}` : ""}</span>
              <b className="tabular-nums whitespace-nowrap">{whole(p.amount)}</b>
              <PaymentDelete doc={doc} p={p} base={base} onChanged={(d) => { onChanged(d); setUndo(null); }} />
            </div>
          ))}
        </div>
      )}
      {undo && (
        <div className="border-t border-border px-3 md:px-4 py-2.5 text-[13px] flex items-center gap-3 bg-success/[0.06]" role="status">
          <CheckCircle2 size={16} className="text-success shrink-0" /><span className="flex-1 min-w-0">{undo.text}</span>
          <button type="button" className="font-semibold text-brand hover:underline" onClick={doUndo} data-testid="doc-pay-undo">Vrátit</button>
        </div>
      )}
      <OffsetDialog offset={offset} docs={offset?.coll === "documents" ? offset.list : []} issued={offset?.coll === "issued_invoices" ? offset.list : []}
        onClose={() => setOffset(null)}
        onDone={async () => {
          setOffset(null);
          try {
            const { data } = await api.get(issued ? "/issued-invoices" : `/documents/${doc.id}`);
            const fresh = issued ? (data || []).find((x) => x.id === doc.id) : data;
            if (fresh) onChanged({ ...fresh, paid_total: fresh.paid_total ?? (fresh.payments || []).reduce((t, p) => t + (p.amount || 0), 0) });
          } catch { /* seznam se obnoví při zavření */ }
        }} />
    </section>
  );
}

function PaymentDelete({ doc, p, base, onChanged }) {
  const del = async () => {
    if (!await ask(`Smazat úhradu ${whole(p.amount)} z ${formatDate(p.date)}?`)) return;
    try { const { data } = await api.delete(`${base}/${doc.id}/payments/${p.id}`); onChanged(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return <button type="button" onClick={del} className="text-muted-foreground hover:text-destructive p-1 shrink-0" aria-label="Smazat úhradu" data-testid={`del-payment-${p.id}`}><Trash2 size={15} /></button>;
}

// Položky po řádcích: zaškrtnuté položky se nastavují hromadně (bez výběru všechny),
// u každé kategorie, zakázka (s barevnou tečkou) a spotřeba; dole proužek rozdělení.
function LineItems({ doc, setDoc, projects, cats, mobile }) {
  const fm = moneyFor(doc), fw = wholeFor(doc);   // 321: neuložený doklad v cizí měně ukazuje částky v ní
  const lines = useMemo(() => doc.line_items || [], [doc.line_items]);
  const [sel, setSel] = useState(() => new Set());
  // 371 (varianta B): položka na jednom řádku — podle šířky (panel vedle tabulky, okno, telefon):
  // „full“ = zakázka, kategorie i spotřeba v řádku; „row“ = v řádku zakázka, ostatní v podřádku a po rozkliknutí;
  // „stack“ = telefon a úzký panel, pole pod sebou. Rozkliknutý řádek podřádek nemá — hodnoty jsou v polích.
  const [openLine, setOpenLine] = useState(null);
  useEffect(() => { setSel(new Set()); setOpenLine(null); }, [doc.id]);
  const boxRef = useRef(null);
  const [boxW, setBoxW] = useState(0);
  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    setBoxW(Math.round(el.getBoundingClientRect().width));
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([e]) => setBoxW(Math.round(e.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, [lines.length > 0]); // eslint-disable-line react-hooks/exhaustive-deps
  const layout = mobile ? "stack" : !boxW || boxW >= 820 ? "full" : boxW >= 480 ? "row" : "stack";
  const full = layout === "full";
  const color = useMemo(() => {
    const m = {}; let k = 0;
    lines.forEach((li) => { if (li.project_id && !(li.project_id in m)) m[li.project_id] = PROJECT_COLORS[k++ % PROJECT_COLORS.length]; });
    return m;
  }, [lines]);
  const setLine = (i, k, v) => setDoc((d) => ({ ...d, line_items: d.line_items.map((li, idx) => (idx === i ? { ...li, [k]: v } : li)) }));
  // 277: hromadná „Spotřeba“ naskladněné řádky přeskočí — server by je odmítl.
  const setMany = (k, v) => setDoc((d) => ({ ...d, line_items: d.line_items.map((li, idx) => ((!sel.size || sel.has(idx)) && !(k === "stock_status" && li.stock_received) ? { ...li, [k]: v } : li)) }));
  // Doklad bez načtených položek (starší sken, OCR je nenašlo): položku jde přidat ručně
  // nebo vzít celou částku bez DPH jako jednu položku — jinak by rozdělení nešlo použít.
  // 279: nový řádek dostane sazbu dokladu (21 / 12 / 0 %), když z hlavičky jde poznat; jinak „neuvedeno“.
  const headerRate = (d) => {
    const net = nf(d.total_without_vat), vat = nf(d.vat_amount);
    if (!net) return null;
    const r = Math.round((vat / net) * 100);
    return [0, 12, 21].includes(r) ? r : null;
  };
  // 287: ručně přidaná položka bez sazby z hlavičky vezme sazbu předchozí položky, jinak 21 %.
  const lastRate = (d) => { const l = d.line_items || []; return l.length && l[l.length - 1].vat_rate != null ? l[l.length - 1].vat_rate : 21; };
  const addLine = (whole) => { if (!whole) setOpenLine(lines.length); return setDoc((d) => ({ ...d, line_items: [...(d.line_items || []), {
    description: whole ? (d.category || "Celá faktura") : "", quantity: 1, unit: "ks",
    line_total: whole ? nf(d.total_without_vat) : 0, unit_price: whole ? nf(d.total_without_vat) : 0, category: d.category || cats[0],
    project_id: d.project_id || null, stock_status: "direct", vat_rate: whole ? headerRate(d) : (headerRate(d) ?? lastRate(d)) }] })); };
  const removeLine = (i) => { setDoc((d) => docWithLines(d, d.line_items.filter((_, idx) => idx !== i))); setSel(new Set()); setOpenLine(null); };
  // 287: množství, cena za MJ, celkem a sazba přepočtou řádek i součty dokladu.
  const setAmount = (i, k, v) => setDoc((d) => docWithLines(d, d.line_items.map((li, idx) => (idx === i ? lineWithAmount(li, k, v) : li))));
  const totals = linesTotals(realLines(lines));
  const follows = headerFollowsLines(doc);
  const unitInput = (li, i, cls) => (
    <Input value={li.unit ?? ""} onChange={(e) => setLine(i, "unit", e.target.value)} list="pp-units" autoComplete="off"
      className={`w-full min-w-0 px-2 ${cls}`} aria-label="Měrná jednotka" data-testid={`line-unit-${i}`} />
  );
  const rateSelect = (li, i, cls) => (
    <Select value={li.vat_rate == null ? "none" : String(li.vat_rate)} onValueChange={(v) => setAmount(i, "vat_rate", v === "none" ? null : v)}>
      <SelectTrigger className={`w-full min-w-0 px-2 ${cls}`} aria-label="Sazba DPH" data-testid={`line-rate-${i}`}><SelectValue /></SelectTrigger>
      <SelectContent>{VAT_RATES.map((r) => <SelectItem key={r} value={String(r)}>{r} %</SelectItem>)}<SelectItem value="none">neuvedeno</SelectItem></SelectContent>
    </Select>
  );
  const lbl = (t, el) => <label className="block min-w-0"><span className="block text-[12px] text-muted-foreground mb-1">{t}</span>{el}</label>;
  const toggle = (i) => setSel((s) => { const n = new Set(s); if (n.has(i)) n.delete(i); else n.add(i); return n; });
  const all = sel.size === lines.length && lines.length > 0;
  const pname = (id) => projects.find((p) => p.id === id)?.name || "Zakázka";
  const split = {};
  lines.forEach((li) => { const k = li.project_id || "_r"; split[k] = (split[k] || 0) + nf(li.line_total); });
  const tot = Object.values(split).reduce((a, b) => a + b, 0);
  // 376: zakázka položky s hledáním (jako hlavní Zakázka)
  const projSelect = (value, onChange, testid, cls = "") => (
    <PropSelect mobile={mobile} title="Zakázka položky" options={projectOptions(projects, value || null, "Režie (bez zakázky)")} onPick={onChange}
      search placeholder="Hledat zakázku, číslo, adresu…" testid={`${testid}-pick`} triggerTestid={testid}
      triggerClass={`${cls.includes("h-") ? "" : "h-10"} ${cls}`} triggerTitle={value ? pname(value) : "Režie (bez zakázky)"}>
      <span className="flex items-center gap-2 min-w-0"><i className="w-2 h-2 rounded-full shrink-0" style={{ background: value ? color[value] || PROJECT_COLORS[0] : REZIE_COLOR }} />
        <span className="truncate">{value ? projLabel(projects.find((p) => p.id === value) || { name: pname(value) }) : "Režie (bez zakázky)"}</span></span>
    </PropSelect>
  );
  const catSelect = (value, onChange, cls = "", testid) => (
    <Select value={value || cats[0]} onValueChange={onChange}><SelectTrigger className={`${cls.includes("h-") ? "" : "h-10"} ${cls}`} data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
  );
  // 277: naskladněný řádek už nejde přepnout ani smazat — na zakázku jde výdejem ze skladu.
  const stockSelect = (value, onChange, testid, cls = "", received = false) => (received
    ? <div className={`${cls.includes("h-") ? "" : "h-10"} px-3 inline-flex items-center rounded-md border border-border bg-secondary/50 text-sm font-semibold text-muted-foreground ${cls}`}
        title="Naskladněno — na zakázku jde výdejem ze skladu" data-testid={testid}>Naskladněno</div>
    : <Select value={value || "direct"} onValueChange={onChange}><SelectTrigger className={`${cls.includes("h-") ? "" : "h-10"} ${cls}`} data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent><SelectItem value="direct">Přímá spotřeba</SelectItem><SelectItem value="stock">Naskladnit</SelectItem></SelectContent></Select>
  );
  const bulkKey = sel.size ? `${sel.size}` : "all";
  if (!lines.length) {
    return (
      <div className="px-4 py-6 border-t border-border text-center" data-testid="lines-empty">
        <div className="font-semibold">Faktura nemá načtené položky</div>
        <p className="text-sm text-muted-foreground mt-1 mb-4">Vezmi celou částku jako jednu položku, nebo položky přidej ručně a rozděl je na zakázky.</p>
        <div className="flex flex-col md:flex-row md:justify-center gap-2">
          <Button onClick={() => addLine(true)} className="w-full md:w-auto h-11 rounded-full font-bold" data-testid="lines-whole">
            Celá částka jako 1 položka{nf(doc.total_without_vat) ? ` · ${fw(nf(doc.total_without_vat))}` : ""}
          </Button>
          <Button variant="outline" onClick={() => addLine(false)} className="w-full md:w-auto h-11 rounded-full font-bold" data-testid="lines-add-empty"><Plus size={16} /> Přidat položku</Button>
        </div>
      </div>
    );
  }
  const COLS = full ? "grid-cols-[22px_minmax(0,1fr)_170px_132px_132px_112px_32px]" : "grid-cols-[22px_minmax(0,1fr)_170px_112px_32px]";
  return (
    <div ref={boxRef}>
      <div className="flex flex-wrap items-center gap-2 px-3 md:px-4 py-3 bg-secondary/50 border-y border-border">
        <span className={`text-sm font-semibold text-muted-foreground w-full ${full ? "xl:w-auto" : ""}`}>{sel.size ? `Vybraným (${sel.size}) nastavit:` : "Všem položkám nastavit:"}</span>
        {/* 371: v užším panelu tři výběry vedle sebe na celou šířku pod nadpisem */}
        <PropSelect mobile={mobile} title="Zakázka pro položky" options={projectOptions(projects, undefined, "Režie (bez zakázky)").map((o) => ({ ...o, on: false }))}
          onPick={(v) => setMany("project_id", v)} search placeholder="Hledat zakázku, číslo, adresu…" testid="apply-remaining-pick" triggerTestid="apply-remaining"
          triggerClass={`h-10 ${layout === "row" ? "flex-1 min-w-0" : "w-full md:w-48"}`}><span className="truncate text-muted-foreground">Zakázka…</span></PropSelect>
        <Select key={`c${bulkKey}`} onValueChange={(v) => setMany("category", v)}>
          <SelectTrigger className={`h-10 flex-1 min-w-0 ${layout === "row" ? "" : "md:flex-none md:w-36"}`} data-testid="apply-category"><SelectValue placeholder="Kategorie…" /></SelectTrigger>
          <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Select key={`s${bulkKey}`} onValueChange={(v) => setMany("stock_status", v)}>
          <SelectTrigger className={`h-10 flex-1 min-w-0 ${layout === "row" ? "" : "md:flex-none md:w-36"}`} data-testid="apply-stock"><SelectValue placeholder="Spotřeba…" /></SelectTrigger>
          <SelectContent><SelectItem value="direct">Přímá spotřeba</SelectItem><SelectItem value="stock">Naskladnit</SelectItem></SelectContent>
        </Select>
      </div>
      {layout !== "stack" && (
        <div className={`grid ${COLS} gap-2 items-center px-4 py-2.5 text-[13px] font-medium text-muted-foreground border-b border-border`}>
          <Checkbox checked={all} onCheckedChange={() => setSel(all ? new Set() : new Set(lines.map((_, i) => i)))} aria-label="Vybrat vše" data-testid="lines-select-all" />
          <span className="pl-2">Položka</span><span className="pl-2">Zakázka</span>
          {full && <><span className="pl-2">Kategorie</span><span className="pl-2">Spotřeba</span></>}
          <span className="text-right pr-2">Bez DPH</span><span />
        </div>
      )}
      <div className="divide-y divide-border">
        {lines.map((li, i) => layout === "stack" ? (
          <div key={i} className="p-3 space-y-2" data-testid={`line-${i}`}>
            <div className="flex items-start gap-2.5">
              <Checkbox checked={sel.has(i)} onCheckedChange={() => toggle(i)} className="mt-1" aria-label="Vybrat položku" data-testid={`line-sel-${i}`} />
              <div className="flex-1 min-w-0">
                <div className="flex gap-1.5">
                  <Input value={li.description || ""} onChange={(e) => setLine(i, "description", e.target.value)} className="h-10 text-base flex-1 min-w-0" />
                  <button type="button" onClick={() => removeLine(i)} disabled={!!li.stock_received} aria-label="Smazat položku" data-testid={`line-del-${i}`}
                    className="h-10 w-10 shrink-0 disabled:opacity-30 inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-destructive"><Trash2 size={16} /></button>
                </div>
                <div className="grid grid-cols-[minmax(0,1fr)_88px] gap-2 mt-2">
                  {lbl("Množství", <NumInput value={li.quantity} onChange={(v) => setAmount(i, "quantity", v)} className="h-10 w-full min-w-0 text-base tabular-nums" data-testid={`line-qty-${i}`} />)}
                  {lbl("MJ", unitInput(li, i, "h-10 text-base"))}
                  {lbl("Cena za MJ bez DPH", <NumInput value={lineUnitPrice(li)} onChange={(v) => setAmount(i, "unit_price", v)} className="h-10 w-full min-w-0 text-right tabular-nums text-base" data-testid={`line-price-${i}`} />)}
                  {lbl("DPH", rateSelect(li, i, "h-10"))}
                </div>
                <div className="flex items-center justify-between gap-2 mt-2">
                  <span className="text-sm text-muted-foreground">Celkem bez DPH</span>
                  <NumInput value={li.line_total} onChange={(v) => setAmount(i, "line_total", v)} className="h-10 w-40 text-right tabular-nums text-base font-semibold" aria-label="Celkem bez DPH" data-testid={`line-total-${i}`} />
                </div>
              </div>
            </div>
            {projSelect(li.project_id, (v) => setLine(i, "project_id", v), `line-project-${i}`, "w-full")}
            <div className="grid grid-cols-2 gap-2">{catSelect(li.category, (v) => setLine(i, "category", v))}{stockSelect(li.stock_status, (v) => setLine(i, "stock_status", v), `line-stock-${i}`, "", li.stock_received)}</div>
          </div>
        ) : (() => {
          const isOpen = openLine === i;
          const g = isOpen ? "" : GHOST;
          const qty = Number(li.quantity || 0).toLocaleString("cs-CZ");
          const subParts = [
            ...(full ? [] : [li.category || cats[0], li.stock_received ? "naskladněno" : li.stock_status === "stock" ? "naskladnit" : "přímá spotřeba"]),
            `${qty} ${li.unit || ""} × ${fm(lineUnitPrice(li))}`.replace(/\s+×/, " ×"),
            li.vat_rate != null ? `DPH ${li.vat_rate} %` : null,
          ].filter(Boolean);
          return (
            <div key={i} className={`grid ${COLS} gap-x-2 gap-y-1.5 items-center px-4 py-2 ${isOpen ? "bg-secondary/40" : ""}`} data-testid={`line-${i}`}>
              <Checkbox checked={sel.has(i)} onCheckedChange={() => toggle(i)} aria-label="Vybrat položku" data-testid={`line-sel-${i}`} />
              <div className="min-w-0">
                <Input value={li.description || ""} onChange={(e) => setLine(i, "description", e.target.value)} placeholder="Popis položky" title={li.description || ""}
                  className={`h-9 min-w-0 px-2 font-semibold ${g}`} data-testid={`line-desc-${i}`} />
                {!isOpen && (
                  <button type="button" onClick={() => setOpenLine(i)} data-testid={`line-more-${i}`} title="Upravit kategorii, spotřebu, množství, cenu a DPH"
                    className="block w-full text-left px-2 text-[12.5px] text-muted-foreground hover:text-foreground truncate">{subParts.join(" · ")}</button>
                )}
              </div>
              {projSelect(li.project_id, (v) => setLine(i, "project_id", v), `line-project-${i}`, `h-9 w-full min-w-0 ${g}`)}
              {full && catSelect(li.category, (v) => setLine(i, "category", v), `h-9 w-full min-w-0 ${g}`)}
              {full && stockSelect(li.stock_status, (v) => setLine(i, "stock_status", v), `line-stock-${i}`, `h-9 w-full min-w-0 ${g}`, li.stock_received)}
              <NumInput value={li.line_total} onChange={(v) => setAmount(i, "line_total", v)} className={`h-9 text-right tabular-nums font-semibold ${g}`} aria-label="Celkem bez DPH" data-testid={`line-total-${i}`} />
              <button type="button" onClick={() => removeLine(i)} disabled={!!li.stock_received} aria-label="Smazat položku"
                title={li.stock_received ? "Naskladněný řádek nejde smazat" : "Smazat položku"} data-testid={`line-del-${i}`}
                className="disabled:opacity-30 h-8 w-8 inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10"><Trash2 size={15} /></button>
              {isOpen && (
                <div className="flex flex-wrap items-center gap-1.5 min-w-0 text-sm text-muted-foreground pb-1" style={{ gridColumn: "2 / -1" }} data-testid={`line-edit-${i}`}>
                  {!full && <div className="w-40">{catSelect(li.category, (v) => setLine(i, "category", v), "h-8")}</div>}
                  {!full && <div className="w-40">{stockSelect(li.stock_status, (v) => setLine(i, "stock_status", v), `line-stock-${i}`, "h-8 w-full", li.stock_received)}</div>}
                  <NumInput value={li.quantity} onChange={(v) => setAmount(i, "quantity", v)} className="h-8 w-20 text-right tabular-nums" aria-label="Množství" data-testid={`line-qty-${i}`} />
                  <div className="w-20">{unitInput(li, i, "h-8")}</div>
                  <span aria-hidden>×</span>
                  <NumInput value={lineUnitPrice(li)} onChange={(v) => setAmount(i, "unit_price", v)} className="h-8 w-28 text-right tabular-nums" aria-label="Cena za MJ bez DPH" title="Cena za MJ bez DPH" data-testid={`line-price-${i}`} />
                  <span className="ml-1">DPH</span>
                  <div className="w-[104px]">{rateSelect(li, i, "h-8")}</div>
                  {li.vat_rate != null && nf(li.line_total) !== 0 && (
                    <span className="tabular-nums whitespace-nowrap">s DPH {fm(Math.round(nf(li.line_total) * (100 + Number(li.vat_rate))) / 100)}</span>
                  )}
                  <button type="button" onClick={() => setOpenLine(null)} data-testid={`line-done-${i}`}
                    className="ml-auto h-8 px-3 rounded-full text-[13px] font-semibold text-foreground hover:bg-secondary">Hotovo</button>
                </div>
              )}
            </div>
          );
        })())}
        <datalist id="pp-units">{["ks", "m", "m²", "m³", "bm", "kg", "t", "l", "bal", "kpl", "sada", "pal", "h"].map((u) => <option key={u} value={u} />)}</datalist>
      </div>
      <div className="px-3 md:px-4 py-2 border-t border-border">
        <button type="button" onClick={() => addLine(false)} data-testid="lines-add" className="h-10 inline-flex items-center gap-2 text-sm font-bold text-brand hover:underline"><Plus size={16} /> Přidat položku</button>
      </div>
      {tot > 0 && (
        <div className="px-3 md:px-4 py-3 bg-secondary/40 border-t border-border" data-testid="split-summary">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-1 md:gap-3 text-sm"><span className="text-muted-foreground">Rozdělení na zakázky</span>
            <span className="tabular-nums" data-testid="lines-totals">Bez DPH <b>{fw(totals.net)}</b> · DPH <b>{fw(totals.vat)}</b> · S DPH <b>{fw(totals.gross)}</b></span></div>
          {!follows && (
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-1 mt-1.5 text-xs text-amber-600 dark:text-amber-400" data-testid="lines-differ">
              <span>Doklad je na {fm(nf(doc.total_without_vat))} bez DPH. Rozdíl {fm(Math.round((nf(doc.total_without_vat) - totals.net) * 100) / 100)} zůstane jako „Nerozděleno“.</span>
              <button type="button" className="self-start md:self-auto font-bold underline hover:no-underline" data-testid="lines-use-totals"
                onClick={() => setDoc((d) => ({ ...d, total_without_vat: totals.net, vat_amount: totals.vat, total_with_vat: totals.gross, line_items: realLines(d.line_items) }))}>Použít součty položek</button>
            </div>
          )}
          <div className="flex gap-[2px] h-2 rounded-full overflow-hidden mt-2">
            {Object.entries(split).filter(([, v]) => v > 0).map(([k, v]) => <span key={k} style={{ width: `${(v / tot) * 100}%`, background: k === "_r" ? REZIE_COLOR : color[k] }} />)}
          </div>
          <div className="flex flex-col md:flex-row md:flex-wrap gap-x-5 gap-y-1 mt-2 text-xs">
            {Object.entries(split).filter(([, v]) => v > 0).map(([k, v]) => (
              <span key={k} className="inline-flex items-center gap-1.5 min-w-0"><i className="w-2 h-2 rounded-full shrink-0" style={{ background: k === "_r" ? REZIE_COLOR : color[k] }} />
                <span className="truncate text-muted-foreground">{k === "_r" ? "Režie" : pname(k)}</span><b className="tabular-nums ml-1">{fw(v)}</b></span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// 343: `nav` = { index, count, onGo } — detail otevřený ze seznamu: „2 / 5", ↑ ↓ další doklad, mezerník zavře.
// 370: `docked` = panel vedle tabulky (Ekonomika › Doklady na PC, jako Úkoly) místo vysouvacího okna;
// rodič ho obalí DetailDock. `onFileShown(bool)` — ukázaná faktura schová tabulku; `goRef` — klik na jiný
// řádek tabulky projde stejnou kontrolou neuložených změn jako ↑ ↓.
export function DocumentReviewDialog({ open, onOpenChange, doc, setDoc, projects, cats, busy, onSave, onDiscard, onDelete, onTypeChange, onBecomeReceipt, banner, blocked, onShowExisting, onReload, nav, docked = false, onFileShown, goRef }) {
  const mobile = useIsMobile();
  const [tab, setTab] = useState("data");          // mobil: Údaje / Faktura
  // PC: náhled souboru vlevo. 344: uložený doklad se otevře bez něj (tlačítko „Faktura“ ho ukáže),
  // čerstvý sken s ním — údaje z OCR se kontrolují proti dokladu. Při listování ↑↓ zůstane, jak je.
  const [showFile, setShowFile] = useState(false);
  // 370: v panelu vedle tabulky zůstává `open` při listování — nový sken po uloženém dokladu ukáže fakturu znovu
  const isNewDoc = !!doc && !doc.id;
  useEffect(() => { if (open) setShowFile(isNewDoc); }, [open, isNewDoc]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k, v) => setDoc((d) => ({ ...d, [k]: v }));
  // 365: štítek dokladu (na co — Střecha, Elektro, Vícepráce) vedle kategorie; jen ekonomické role
  const { user: me } = useAuth();
  const canTag = ["OWNER", "MANAGER", "ASSISTANT", "ACCOUNTANT"].includes(me?.role);
  const [tagsMgr, setTagsMgr] = useState(false);
  // `ui_type` je jen stav dialogu — NEplést s backendovým `doc_type`
  // ("faktura" / "uctenka"). Vydaná faktura je jiná entita (`/issued-invoices`):
  // protistrana je odběratel a rozpad na zakázky/kategorie se u výnosu neřeší.
  const issued = doc?.ui_type === "vydana";
  // 366: nová vydaná faktura — odběratel, IČO a DIČ z investora vybrané zakázky. Doplní se, dokud
  // je odběratel prázdný nebo ho vyplnil investor; jiná zakázka ho vymění, přepnutí na přijatou smaže.
  const setThem = (k, v) => setDoc((d) => ({ ...d, [k]: v, party_from_investor: null }));
  const { data: investors } = useQuery({ queryKey: ["investors"], queryFn: () => api.get("/investors").then((r) => r.data || []),
    enabled: !!open && issued && !!doc && !doc.id, staleTime: 60_000 });
  const projectInvId = (projects || []).find((p) => p.id === doc?.project_id)?.investor_id || null;
  const investor = projectInvId ? (investors || []).find((i) => i.id === projectInvId) : null;
  useEffect(() => {
    if (!open || !doc || doc.id) return;
    const auto = doc.party_from_investor;
    const clear = { supplier_name: "", supplier_ico: null, supplier_dic: null, party_from_investor: null };
    if (!issued) { if (auto) setDoc((d) => ({ ...d, ...clear })); return; }
    if (!investor) { if (auto && auto !== doc.project_id && (!projectInvId || investors)) setDoc((d) => ({ ...d, ...clear })); return; }
    if (auto === doc.project_id || (!auto && (doc.supplier_name || "").trim())) return;
    setDoc((d) => ({ ...d, supplier_name: investor.name || "", supplier_ico: investor.ico || null, supplier_dic: investor.dic || null,
                     party_from_investor: d.project_id }));
  }, [open, issued, doc?.id, doc?.project_id, doc?.party_from_investor, investor, projectInvId, investors]); // eslint-disable-line react-hooks/exhaustive-deps
  const onType = (v) => {
    if (v === "uctenka") return onBecomeReceipt?.();
    const base = (v === "vydana" || v === "dobropis_vydany") ? "vydana" : "prijata";
    const baseChanged = (base === "vydana") !== issued;
    // Přijatá↔vydaná v Ekonomice mění entitu (documents ↔ issued-invoices) — řeší parent.
    if (baseChanged && onTypeChange) {
      onTypeChange(base);
      if (!doc.id) setDoc((d) => ({ ...d, is_credit_note: v.startsWith("dobropis") }));
    } else {
      setDoc((d) => applyDocType(d, v));
    }
  };
  // Sledování změn (160): snapshot formuláře při otevření uloženého dokladu.
  // „Uložit změny" je aktivní jen když se něco změnilo; zavření s neuloženými
  // změnami se zeptá. Úhrady (157) se ukládají hned a snapshot rovnou přepíšou.
  const num = (v) => Number(v) || 0;
  const formSnapshot = (d) => {
    if (!d) return "";
    const base = {
      payment_status: d.payment_status, paid_date: d.paid_date || null, paid_method: d.paid_method || null,
      supplier_name: d.supplier_name || "", supplier_ico: d.supplier_ico || "", supplier_dic: d.supplier_dic || "",
      invoice_number: d.invoice_number || "", variable_symbol: d.variable_symbol || "",
      issue_date: (d.issue_date || "").slice(0, 10), due_date: (d.due_date || "").slice(0, 10),
      total_without_vat: num(d.total_without_vat), vat_amount: num(d.vat_amount), total_with_vat: num(d.total_with_vat),
      currency: (d.currency || "").toUpperCase() || null, fx_rate: d.fx_rate ?? null,      // 321
      is_credit_note: !!d.is_credit_note, credited_invoice_number: d.credited_invoice_number || "",
      credits: d.credits_invoice_id || d.credits_document_id || null,
      tag: d.tag || null,     // 368: změna štítku rozsvítí „Uložit změny“
    };
    if (d.ui_type === "vydana") {
      base.project_id = d.project_id || null; base.invoice_type = d.invoice_type || null; base.note = d.note || "";
      base.advance_ids = [...(d.advance_ids || [])].sort().join(",");   // odečtené zálohy (171)
    } else {
      base.invoice_type = d.invoice_type || null;     // 291: druh, odečtené zálohy a K úhradě i u přijaté
      base.amount_due = d.amount_due === "" || d.amount_due == null ? null : num(d.amount_due);
      base.advance_ids = [...(d.advance_ids || [])].sort().join(",");
      base.assignment_mode = d.assignment_mode; base.project_id = d.project_id || null; base.category = d.category || null;
      // 401: i sazba DPH a jednotka — jinak jejich změna nerozsvítila „Uložit změny“
      base.line_items = (d.line_items || []).map((li) => ({ description: li.description || "", quantity: num(li.quantity), unit: li.unit || "", line_total: num(li.line_total),
        vat_rate: li.vat_rate == null || li.vat_rate === "" ? null : num(li.vat_rate), category: li.category || null, project_id: li.project_id || null, stock_status: li.stock_status || "direct" }));
    }
    return JSON.stringify(base);
  };
  const snapRef = useRef("");
  const [confirmClose, setConfirmClose] = useState(false);
  const [, setSnapTick] = useState(0);
  useEffect(() => {
    if (open && doc) { snapRef.current = formSnapshot(doc); setSnapTick((t) => t + 1); }   // 343: překreslit — „Uložit změny" jinak svítí i bez změny
    if (open) setTab("data");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, doc?.id]);
  const dirty = doc?.id ? formSnapshot(doc) !== snapRef.current : false;
  // 343: při zavření / přechodu porovnat až teď — snímek se bere v efektu po prvním vykreslení,
  // takže `dirty` z toho vykreslení by hned po otevření (mezerník, šipka) hlásil změnu, která není.
  const isDirty = () => !!doc?.id && formSnapshot(doc) !== snapRef.current;
  const handleOpenChange = (o) => {
    if (o) return onOpenChange(true);
    if (isDirty()) { setConfirmClose(true); return; }
    onOpenChange(false);
  };
  // 343: přechod na jiný doklad s neuloženými změnami se zeptá stejně jako zavření
  const [pendingGo, setPendingGo] = useState(null);
  const go = (i) => { if (isDirty()) { setPendingGo(i); setConfirmClose(true); return; } nav.onGo(i); };
  const docNav = nav && doc?.id ? { ...nav, onGo: go } : null;
  useDetailNav(docNav, open && !confirmClose, () => handleOpenChange(false), { dialogs: docked ? 0 : 1, letters: false });
  if (goRef) goRef.current = docNav ? go : null;
  useDockEscape(() => handleOpenChange(false), docked && !!open && !!doc && !confirmClose);
  useEffect(() => { if (docked) onFileShown?.(!!(open && doc?.file_url && showFile)); }, [docked, open, doc?.file_url, showFile]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { if (docked) onFileShown?.(false); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const cardRef = useRef(null);
  useEffect(() => { if (docked && open) cardRef.current?.focus?.({ preventScroll: true }); }, [docked, open]);
  // 371: Z otevře výběr zakázky (jako L štítek) — ne při psaní, v rozbalené nabídce ani u rozdělených položek
  // 376: K výběr kategorie (u přijaté)
  const [pick, setPick] = useState(null);
  const zOn = !!open && !!doc && !mobile && !confirmClose && !tagsMgr && (issued || doc?.assignment_mode !== "split");
  useEffect(() => {
    if (!zOn) return undefined;
    const onZ = propHotkey("z", () => setPick("prj")), onK = propHotkey("k", () => { if (!issued) setPick("cat"); });
    const onKey = (e) => { onZ(e); onK(e); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [zOn, issued]);   // 344: kurzor do panelu — mezerník a šipky patří detailu
  const total = payableTotal(doc);   // konečná faktura: doplatek po zálohách (171)
  const paidTotal = doc?.paid_total ?? (doc?.payments || []).reduce((s, p) => s + (p.amount || 0), 0);
  const applyPaymentChange = (data) => {
    setDoc((d) => { const nd = { ...d, payments: data.payments, paid_total: data.paid_total, payment_status: data.payment_status }; snapRef.current = formSnapshot(nd); return nd; });
    onReload?.();
  };

  const n = nf(doc?.total_without_vat), v = nf(doc?.vat_amount), g = nf(doc?.total_with_vat);
  const rate = n > 0 && v > 0 && [12, 21].includes(Math.round((v / n) * 100)) ? Math.round((v / n) * 100) : null;   // 287: smíšené sazby → jen „DPH“
  const sign = doc?.is_credit_note ? -1 : 1;
  const fm = moneyFor(doc), fw = wholeFor(doc);
  const party = issued ? "Odběratel" : "Dodavatel";
  const sub = [doc?.supplier_ico && `IČO ${doc.supplier_ico}`, doc?.supplier_dic && `DIČ ${doc.supplier_dic}`,
    doc?.invoice_number && `${doc?.is_credit_note ? "dobropis" : "faktura"} č. ${doc.invoice_number}`].filter(Boolean).join(" · ");
  const numberLabel = doc?.internal_no ? `${issued ? "FV" : "FP"} ${doc.internal_no}` : null;
  const inp = "h-11 md:h-10 text-base md:text-sm w-full min-w-0";
  const splitProjects = new Set((doc?.line_items || []).map((li) => li.project_id || "_r")).size;
  const overdue = !!doc?.due_date && !doc?.is_credit_note && daysTo(doc.due_date) < 0 && total - paidTotal > 0.5;
  // 286: IČO z registru ARES doplní název a DIČ protistrany.
  const fromAres = (a) => {
    setDoc((d) => ({ ...d, supplier_ico: a.ico, supplier_name: a.name || d.supplier_name, supplier_dic: a.dic || "", party_from_investor: null }));
    if (!issued && a.vat_payer === false) toast.info("Dodavatel podle registru není plátce DPH.");
  };

  const data = doc && (
    <div className="space-y-5">
      {banner}
      <NotInvoiceHint doc={doc} setDoc={setDoc} />
      <div>
        {/* 368: štítek nad dodavatelem — klik / L otevře seznam (PC pod nadpiskem, telefon spodní okno) */}
        <DocTagEyebrow value={doc.tag || null} onChange={(t) => set("tag", t)} onManage={() => setTagsMgr(true)}
          mobile={mobile} readOnly={!canTag} hotkey={!!open && !confirmClose && !tagsMgr} />
        <h2 className="font-heading font-extrabold text-[22px] md:text-[26px] tracking-tight leading-tight break-words" data-testid="doc-party-name">{doc.supplier_name || `${party} neuveden`}</h2>
        {sub && <p className="text-sm text-muted-foreground mt-0.5">{sub}</p>}
        <DirectionHint doc={doc} />
        {/* 291: druh dokladu — malý výběr (dobropis je druh, ne směr) */}
        <DocKindRow doc={doc} setDoc={setDoc} className="mt-3" />
        {/* 291: konečná od dodavatele odečítá jeho zálohy jako vydaná; bez záloh v systému K úhradě ručně */}
        {!issued && <AdvancesSection doc={doc} setDoc={setDoc} className="mt-3" />}
        <TaxDocPicker doc={doc} setDoc={setDoc} className="mt-3" />
        <TaxDocNote doc={doc} className="mt-3" />
        {doc.is_credit_note && (doc.credits_document_id || doc.credits_invoice_id || doc.credited_invoice_number) && (
          <div className="mt-3 flex items-center gap-3 rounded-[14px] border border-border bg-card px-3.5 py-2.5" data-testid="credit-origin">
            <Undo2 size={18} className="text-muted-foreground shrink-0" />
            <div className="min-w-0 flex-1 text-sm"><div className="text-xs text-muted-foreground">Opravuje fakturu</div>
              <div className="font-semibold truncate">č. {doc.credited_invoice_number || "—"}</div></div>
          </div>
        )}
      </div>
      <AmountTiles testid="doc-amounts">
        <AmountTile label="Bez DPH" value={fw(sign * n)} title={fm(sign * n)} />
        <AmountTile label={rate ? `DPH ${rate} %` : "DPH"} value={fw(sign * v)} title={fm(sign * v)} />
        <AmountTile label="Celkem s DPH" value={fw(sign * g)} title={fm(sign * g)} />
        <AmountTile label={doc.is_credit_note ? "Zbývá vyrovnat" : doc.advances_deducted_gross > 0 || (!issued && doc.invoice_type === "konecna" && doc.amount_due != null && doc.amount_due !== "") ? "K úhradě po zálohách" : "K úhradě"} value={fw(doc.id ? Math.max(total - paidTotal, 0) : total)} title={fm(doc.id ? Math.max(total - paidTotal, 0) : total)} tone="brand" testid="doc-to-pay" />
      </AmountTiles>
      {/* 371 (varianta B): vlastnosti jako řádky — zakázka (u přijaté i kategorie); klávesa Z otevře výběr zakázky */}
      <section className="space-y-0.5" data-testid="doc-props">
        <PropRow label="Zakázka" kbd={mobile || (!issued && doc.assignment_mode === "split") ? null : "Z"}>
          {!issued && doc.assignment_mode === "split" ? (
            <span className="h-9 inline-flex items-center gap-2 font-semibold min-w-0" data-testid="doc-prop-split">
              <Layers size={16} className="text-muted-foreground shrink-0" /><span className="truncate">Rozděleno po položkách · {plur(splitProjects, "zakázka", "zakázky", "zakázek")}</span>
            </span>
          ) : (
            <PropSelect open={pick === "prj"} onOpenChange={(o) => setPick(o ? "prj" : null)} mobile={mobile} title="Zakázka" icon={Briefcase}
              valueLabel={doc.project_id ? (projLabel(projects.find((p) => p.id === doc.project_id) || { name: doc.project_name || "Zakázka" })) : issued ? "Bez zakázky" : "Režie (bez zakázky)"}
              options={projectOptions(projects, doc.project_id || null, issued ? "Bez zakázky" : "Režie (bez zakázky)")} onPick={(val) => set("project_id", val)}
              search placeholder="Hledat zakázku, číslo, adresu…" testid="doc-pick-proj" triggerTestid={issued ? "issued-project" : "whole-project"} />
          )}
        </PropRow>
        {!issued && (
          <PropRow label="Kategorie" kbd={mobile || doc.assignment_mode === "split" ? null : "K"}>
            {doc.assignment_mode === "split" ? (
              <span className="h-9 inline-flex items-center gap-2 font-semibold"><Layers size={16} className="text-muted-foreground shrink-0" />podle položek</span>
            ) : (
              <PropSelect open={pick === "cat"} onOpenChange={(o) => setPick(o ? "cat" : null)} mobile={mobile} title="Kategorie" icon={Shapes} width={260}
                valueLabel={doc.category || cats[0]} options={catOptions(cats, doc.category || cats[0])} onPick={(val) => set("category", val)}
                search={cats.length > 9} placeholder="Hledat kategorii…" testid="doc-pick-cat" triggerTestid="whole-category" />
            )}
          </PropRow>
        )}
      </section>

      {issued ? (
        <section>
          <h3 className="text-sm font-semibold text-muted-foreground mb-2">{doc.invoice_type === "konecna" ? "Odečtené zálohy a poznámka" : "Poznámka"}</h3>
          <div className="rounded-[18px] border border-border bg-card p-3 md:p-4 space-y-3">
            <Field label="Poznámka"><Input value={doc.note || ""} onChange={(e) => set("note", e.target.value)} className={inp} data-testid="issued-note" /></Field>
            <AdvancesSection doc={doc} setDoc={setDoc} />
            <p className="text-[13px] text-muted-foreground">{!doc.project_id ? "Vydaná faktura je výnos — nerozděluje se na nákladové kategorie ani položky."
              : ["zaloha", TAX_DOC].includes(doc.invoice_type) ? "Záloha jde do tržeb zakázky tím, co odběratel zaplatí."
              : `Tržba ${fw(sign * n)} bez DPH se započítá do výnosů zakázky.`}</p>
          </div>
        </section>
      ) : (
        <section>
          <div className="flex items-baseline justify-between gap-3 mb-2">
            <h3 className="text-sm font-semibold text-muted-foreground">Zařazení a položky</h3>
            {(doc.line_items || []).length > 0 && <span className="text-[13px] text-muted-foreground">{plur(doc.line_items.length, "položka", "položky", "položek")} z faktury</span>}
          </div>
          <div className="rounded-[18px] border border-border bg-card overflow-hidden">
            <div className="p-3 md:p-4">
              <div ref={autoPill} className="grid grid-cols-2 gap-1 p-1 rounded-full bg-secondary" role="group" aria-label="Zařazení">
                {[["whole", mobile ? "Celý doklad" : "Celý doklad na 1 zakázku", "mode-whole"], ["split", mobile ? "Po položkách" : "Rozdělit po položkách", "mode-split"]].map(([k, l, t]) => (
                  <button key={k} type="button" onClick={() => set("assignment_mode", k)} data-testid={t} aria-pressed={doc.assignment_mode === k}
                    className={`h-10 rounded-full text-[13px] md:text-sm font-semibold px-2 ${doc.assignment_mode === k ? "bg-card shadow-sm text-foreground" : "text-muted-foreground"}`}>{l}</button>
                ))}
              </div>
            </div>
            {doc.assignment_mode === "whole" ? (
              <>
                <p className="px-3 md:px-4 pb-3 text-[13px] text-muted-foreground" data-testid="whole-hint">Celý doklad jde na zakázku a kategorii z řádků nahoře. Po rozdělení dostane každá položka svou.</p>
                {(doc.line_items || []).length > 0 && (
                  <div className="border-t border-border divide-y divide-border" data-testid="whole-lines">
                    {doc.line_items.map((li, i) => (
                      <div key={i} className="flex items-center justify-between gap-3 px-3 md:px-4 py-2 text-sm">
                        <span className="min-w-0 truncate text-muted-foreground">{li.description || "Položka"}</span><b className="tabular-nums whitespace-nowrap">{fw(nf(li.line_total))}</b>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : <LineItems doc={doc} setDoc={setDoc} projects={projects} cats={cats} mobile={mobile} />}
          </div>
        </section>
      )}

      {doc.id ? (
        <section>
          <h3 className="text-sm font-semibold text-muted-foreground mb-2">{doc.is_credit_note ? "Vyrovnání" : issued ? "Platby od odběratele" : "Platby"}</h3>
          <PaymentBox doc={doc} setDoc={setDoc} issued={issued} total={total} paid={paidTotal} onChanged={applyPaymentChange} />
        </section>
      ) : doc.invoice_type === TAX_DOC ? null : (   /* 288/291: doklad k platbě = platba, den platby = datum dokladu */
        <section className="rounded-[18px] border border-border bg-card p-3 md:p-4 grid grid-cols-1 md:grid-cols-3 gap-2.5">
          <Field label="Stav platby">
            <Select value={doc.payment_status} onValueChange={(val) => setDoc((d) => (val === "Zaplaceno"
              ? { ...d, payment_status: val, paid_date: d.paid_date || today(), paid_method: d.paid_method || "Převodem" }
              : { ...d, payment_status: val }))}>
              <SelectTrigger className="h-10" data-testid="doc-payment-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="Nezaplaceno">Nezaplaceno</SelectItem><SelectItem value="Zaplaceno">Zaplaceno</SelectItem></SelectContent></Select></Field>
          {doc.payment_status === "Zaplaceno" && (
            <>
              <Field label="Datum úhrady"><Input type="date" value={doc.paid_date || today()} onChange={(e) => set("paid_date", e.target.value)} className={inp} data-testid="paid-date-input" /></Field>
              <Field label="Způsob">
                <Select value={doc.paid_method || "Převodem"} onValueChange={(val) => set("paid_method", val)}>
                  <SelectTrigger className="h-10" data-testid="paid-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select></Field>
            </>
          )}
        </section>
      )}

      {/* 329: platbu zadáváš na faktuře, spárovaná objednávka si ji přečte */}
      {!issued && !doc.is_credit_note && doc.invoice_type !== TAX_DOC && <OrderLinkCard doc={doc} setDoc={setDoc} />}

      <section data-testid="doc-details">
        <h3 className="text-sm font-semibold text-muted-foreground mb-2">Údaje dokladu</h3>
        <DocPartyCards doc={doc} setDoc={setDoc} set={set} setThem={setThem} issued={issued} fromAres={fromAres} overdue={overdue}
          partyLabel={issued && doc.party_from_investor ? <>{party} <span className="text-brand font-semibold" data-testid="doc-party-from-investor">· z investora zakázky</span></> : party} />
      </section>
    </div>
  );

  const footer = doc && (
    <div className={`shrink-0 border-t border-border bg-background/95 backdrop-blur px-4 md:px-6 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex ${doc.id ? "flex-row items-center" : "flex-col-reverse md:flex-row md:items-center"} gap-2`}>
      {!doc.id ? (
        <>
          <Button variant="outline" onClick={onDiscard} data-testid="doc-discard-btn" className="w-full md:w-auto h-11">Zahodit</Button>
          <span className="flex-1" />
          {/* Jistá duplicita (153): uložit nejde — místo Uložit je „Zobrazit existující" */}
          {blocked ? (
            <Button variant="outline" onClick={onShowExisting} data-testid="doc-show-existing-btn" className="w-full md:w-auto h-11 font-bold px-6">Zobrazit existující</Button>
          ) : (
            <Button onClick={onSave} disabled={busy || kindBlocked(doc)} data-testid="doc-save-btn" className="w-full md:w-auto h-11 font-bold px-6"><CheckCircle2 size={18} /> Uložit doklad</Button>
          )}
        </>
      ) : (
        <>
          <Button variant="outline" onClick={onDelete} data-testid="doc-delete-btn" aria-label={issued ? "Smazat fakturu" : "Smazat doklad"} className="w-11 md:w-auto px-0 md:px-4 h-11 shrink-0 font-semibold text-destructive hover:text-destructive"><Trash2 size={17} /><span className="hidden md:inline">{issued ? "Smazat fakturu" : "Smazat doklad"}</span></Button>
          <span className="flex-1 hidden md:block text-right text-[13px] text-muted-foreground truncate">
            {doc.created_at ? `nahráno ${formatDate(doc.created_at)}` : ""}{(doc.payments || []).length ? ` · poslední úhrada ${formatDate(doc.payments[doc.payments.length - 1].date)}` : ""}
          </span>
          <Button onClick={onSave} disabled={busy || !dirty} data-testid="doc-save-btn" className="flex-1 md:flex-none h-11 font-bold px-6"><CheckCircle2 size={18} /> Uložit změny</Button>
        </>
      )}
    </div>
  );

  const header = doc && (
    <div className="shrink-0 flex items-center gap-2 px-4 md:px-6 pt-4 pb-3">
      {/* 291: nahoře jen kdo doklad vystavil; dobropis je druh dokladu (výběr pod protistranou).
          Čerstvý sken, který je ve skutečnosti účtenka (150) — jen nový doklad se souborem. */}
      <div className="flex-1 md:flex-none min-w-0">
        <DirectionToggle issued={issued}
          onChange={(base) => onType(doc.is_credit_note ? (base === "vydana" ? "dobropis_vydany" : "dobropis_prijaty") : base)}
          onReceipt={!doc.id && doc.file_url && onBecomeReceipt ? () => onType("uctenka") : null} />
      </div>
      {numberLabel && <span className="text-sm font-bold tabular-nums text-muted-foreground" title="Interní číslo dokladu" data-testid="doc-internal-no">{numberLabel}</span>}
      <span className="hidden md:block flex-1" />
      {!mobile && doc.file_url && !showFile && (
        <Button type="button" variant="outline" className="h-10 rounded-full" onClick={() => setShowFile(true)} data-testid="doc-show-file"><FileText size={16} /> Faktura</Button>
      )}
      <DetailNav nav={docNav} testid="doc-nav" />
      {docked ? <CloseButton testid="doc-close" onClick={() => handleOpenChange(false)} title="Zavřít (mezerník / Esc)" />
        : <SheetClose asChild><CloseButton testid="doc-close" /></SheetClose>}
    </div>
  );

  const extras = (
    <>
    {canTag && <TagsManagerDialog open={tagsMgr} onOpenChange={setTagsMgr} />}
    {/* Zavření s neuloženými změnami (160) — zeptá se místo tichého zahození. */}
    <AlertDialog open={confirmClose} onOpenChange={(o) => { if (!o) { setConfirmClose(false); setPendingGo(null); } }}>
      <AlertDialogContent data-testid="doc-unsaved-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{pendingGo != null ? "Přejít na další doklad bez uložení?" : "Zavřít bez uložení?"}</AlertDialogTitle>
          <AlertDialogDescription>Máte neuložené změny.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="outline" onClick={() => { setConfirmClose(false); setPendingGo(null); }} data-testid="doc-unsaved-back" className="h-11 w-full sm:w-auto">Zpět</Button>
          <Button variant="destructive" onClick={() => { const i = pendingGo; setConfirmClose(false); setPendingGo(null); if (i != null) nav?.onGo(i); else onOpenChange(false); }}
            data-testid="doc-unsaved-discard" className="h-11 w-full sm:w-auto font-bold">{pendingGo != null ? "Přejít bez uložení" : "Zavřít bez uložení"}</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );

  // 370: panel vedle tabulky — vlevo faktura (po tlačítku Faktura, tabulka se mezitím schová), vpravo údaje
  if (docked) {
    return (
      <>
        {open && doc && (
          <div ref={cardRef} tabIndex={-1} data-testid="doc-review-dialog" aria-label={issued ? "Detail vydané faktury" : "Detail dokladu"}
            className="h-full flex bg-card border border-border rounded-[24px] overflow-hidden shadow-[0_12px_40px_rgba(0,0,0,.10)] outline-none">
            {doc.file_url && showFile && (
              <div className="w-[42%] max-w-[620px] shrink-0 bg-secondary/60 border-r border-border min-h-0">
                <Preview doc={doc} onHide={() => setShowFile(false)} />
              </div>
            )}
            <div className="flex-1 min-w-0 flex flex-col min-h-0">
              {header}
              <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 pb-6">{data}</div>
              {footer}
            </div>
          </div>
        )}
        {extras}
      </>
    );
  }

  return (
    <>
    <Sheet open={open} onOpenChange={handleOpenChange}>
      {/* 344: na PC kurzor do okna (ne do pole — na telefonu by vyjela klávesnice): mezerník a šipky pak patří detailu */}
      <SheetContent side={mobile ? "bottom" : "right"} hideClose data-testid="doc-review-dialog"
        onOpenAutoFocus={(e) => { e.preventDefault(); if (!mobile) e.currentTarget?.focus?.({ preventScroll: true }); }}
        className={mobile
          ? "p-0 h-[94dvh] rounded-t-[24px] flex flex-col pb-0"
          : `p-0 border-0 md:inset-y-3 md:right-3 md:h-auto md:rounded-[24px] overflow-hidden flex flex-row sm:max-w-none ${doc?.file_url && showFile ? "w-[min(1320px,calc(100vw-24px))]" : "w-[min(880px,calc(100vw-24px))]"}`}>
        <SheetTitle className="sr-only">{issued ? "Kontrola vydané faktury" : "Kontrola dokladu"}</SheetTitle>
        <SheetDescription className="sr-only">Faktura</SheetDescription>
        {doc && (mobile ? (
          <>
            <div className="mx-auto mt-2 h-1.5 w-10 rounded-full bg-border shrink-0" aria-hidden />
            {header}
            <div className="px-4 pb-2 shrink-0">
              <div ref={autoPill} className="grid grid-cols-2 gap-1 p-1 rounded-full bg-secondary" role="tablist">
                {[["data", "Údaje"], ["file", doc.file_url && (doc.file_url || "").toLowerCase().includes(".pdf") ? "Faktura (PDF)" : "Faktura"]].map(([k, l]) => (
                  <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`doc-tab-${k}`}
                    className={`h-9 rounded-full text-sm font-semibold ${tab === k ? "bg-card shadow-sm" : "text-muted-foreground"}`}>{l}</button>
                ))}
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
              {tab === "file" ? <Preview doc={doc} /> : <div className="px-4 pb-6">{data}</div>}
            </div>
            {footer}
          </>
        ) : (
          <>
            {doc.file_url && showFile && (
              <div className="w-[36%] max-w-[500px] shrink-0 bg-secondary/60 border-r border-border min-h-0">
                <Preview doc={doc} onHide={() => setShowFile(false)} />
              </div>
            )}
            <div className="flex-1 min-w-0 flex flex-col min-h-0 bg-background">
              {header}
              <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6">{data}</div>
              {footer}
            </div>
          </>
        ))}
      </SheetContent>
    </Sheet>
    {extras}
    </>
  );
}
